/**
 * =====================================================================================
 * SERVICIO DE RECEPTORES - receptores.service.js
 * =====================================================================================
 *
 * Este servicio gestiona los RECEPTORES, que son los clientes que reciben
 * los documentos tributarios electrónicos (DTEs).
 *
 * ¿QUÉ ES UN RECEPTOR?
 * --------------------
 * En el contexto de facturación electrónica, el RECEPTOR es quien recibe la factura.
 * Es decir, es TU CLIENTE - la persona o empresa a quien le vendes.
 *
 * DATOS TÍPICOS DE UN RECEPTOR:
 * -----------------------------
 * - Tipo de Documento (DUI, NIT, Pasaporte, etc.)
 * - Número de Documento
 * - Nombre o Razón Social
 * - NRC (si es contribuyente)
 * - Actividad Económica (si es contribuyente)
 * - Dirección
 * - Teléfono
 * - Correo electrónico
 *
 * TIPOS DE RECEPTOR:
 * ------------------
 *
 * 1. CONSUMIDOR FINAL (persona natural con DUI):
 *    - Se identifica con DUI (Documento Único de Identidad)
 *    - No tiene NRC
 *    - Recibe Factura de Consumidor Final (tipo 01)
 *
 * 2. CONTRIBUYENTE (empresa o persona con NIT):
 *    - Se identifica con NIT (Número de Identificación Tributaria)
 *    - Tiene NRC (Número de Registro de Contribuyente)
 *    - Recibe Comprobante de Crédito Fiscal - CCF (tipo 03)
 *
 * FORMATO DE DOCUMENTOS:
 * ----------------------
 * DUI: 00000000-0 (8 dígitos + guión + 1 dígito verificador)
 * NIT: 0000-000000-000-0 (14 dígitos con guiones)
 * NRC: 000000-0 (6 dígitos + guión + 1 dígito)
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - api.js: Cliente HTTP para las peticiones
 * - src/views/configuracion/ReceptoresView.vue: CRUD de receptores
 * - src/views/emision/FacturaView.vue: Selección de receptor al emitir
 * - src/components/emision/DatosGeneralesFactura.vue: Autocomplete de receptores
 * - Backend: Controllers/ReceptoresController.cs
 *
 * =====================================================================================
 */

// Importamos la instancia de Axios configurada
import api from './api'

/**
 * =====================================================================================
 * OBJETO DEL SERVICIO DE RECEPTORES
 * =====================================================================================
 *
 * Este servicio es más complejo que otros porque incluye:
 * 1. Operaciones CRUD estándar
 * 2. Búsqueda de receptores
 * 3. Mapeo de datos entre frontend y backend (DTOs)
 * 4. Formateo y limpieza de documentos
 *
 * El mapeo es necesario porque el frontend y backend manejan los datos
 * de forma diferente (ej: el frontend muestra documentos con guiones,
 * el backend los almacena sin guiones).
 */
/**
 * Normaliza un término de búsqueda de documentos.
 * Si el usuario escribe un DUI o NIT sin guiones, lo formatea automáticamente.
 *
 * @param {string} searchTerm - Término de búsqueda original
 * @returns {string} Término normalizado con formato de documento si aplica
 */
const normalizeDocumentSearch = (searchTerm) => {
  if (!searchTerm) return searchTerm

  const cleanTerm = searchTerm.replace(/[^0-9]/g, '') // Solo dígitos

  // DUI: 9 dígitos → formatear como XXXXXXXX-X
  if (cleanTerm.length === 9 && /^\d{9}$/.test(cleanTerm)) {
    return `${cleanTerm.slice(0, 8)}-${cleanTerm.slice(8)}`
  }
  // NIT: 14 dígitos → formatear como XXXX-XXXXXX-XXX-X
  if (cleanTerm.length === 14 && /^\d{14}$/.test(cleanTerm)) {
    return `${cleanTerm.slice(0, 4)}-${cleanTerm.slice(4, 10)}-${cleanTerm.slice(10, 13)}-${cleanTerm.slice(13)}`
  }

  return searchTerm
}

const receptoresService = {
  /**
   * ===========================================================================
   * MÉTODO: getAll
   * ===========================================================================
   *
   * Obtiene lista paginada de receptores con búsqueda opcional.
   *
   * ENDPOINT: GET /api/Receptores?page=1&pageSize=10&search=juan
   *
   * PAGINACIÓN:
   * -----------
   * Como puede haber miles de clientes, se cargan por páginas.
   * page=1 significa la primera página (1-indexed, no 0-indexed)
   *
   * @param {Object} params - Parámetros de consulta
   * @param {number} params.page - Número de página (empieza en 1)
   * @param {number} params.pageSize - Cantidad de registros por página
   * @param {string} params.search - Término de búsqueda (opcional)
   * @returns {Promise<Object>} { items: [...], totalCount, page, pageSize }
   */
  async getAll(params = {}) {
    const queryParams = new URLSearchParams()
    if (params.pageNumber) queryParams.append('pageNumber', params.pageNumber)
    if (params.pageSize) queryParams.append('pageSize', params.pageSize)
    if (params.search) queryParams.append('search', normalizeDocumentSearch(params.search))
    if (params.soloActivos !== undefined) queryParams.append('soloActivos', params.soloActivos)
    if (params.orderBy) queryParams.append('orderBy', params.orderBy)
    if (params.orderDirection) queryParams.append('orderDirection', params.orderDirection)
    if (params.fechaDesde) queryParams.append('fechaDesde', params.fechaDesde)
    if (params.fechaHasta) queryParams.append('fechaHasta', params.fechaHasta)
    if (params.soloSujetosExcluidos) queryParams.append('soloSujetosExcluidos', 'true')

    const response = await api.get(`/Receptores?${queryParams.toString()}`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getById
   * ===========================================================================
   *
   * Obtiene un receptor específico por su ID.
   *
   * ENDPOINT: GET /api/Receptores/{id}
   *
   * USO: Obtener datos completos de un receptor para edición o visualización
   *
   * @param {number} id - ID del receptor
   * @returns {Promise<Object>} Datos completos del receptor
   */
  async getById(id) {
    const response = await api.get(`/Receptores/${id}`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: search
   * ===========================================================================
   *
   * Busca receptores por término de búsqueda.
   * Busca en: nombre, número de documento, correo.
   *
   * ENDPOINT: GET /api/Receptores/search?searchTerm=xxx
   *
   * DIFERENCIA CON getAll:
   * - getAll retorna paginado con más metadatos
   * - search retorna array simple para autocompletado
   *
   * USO PRINCIPAL:
   * En el componente DatosGeneralesFactura hay un autocomplete
   * que llama a este método mientras el usuario escribe.
   *
   * @param {string} searchTerm - Término a buscar
   * @returns {Promise<Array>} Lista de receptores que coinciden
   *
   * EJEMPLO:
   * search("juan") → Busca clientes cuyo nombre contenga "juan"
   * search("0614") → Busca clientes cuyo NIT empiece con "0614"
   */
  async search(searchTerm, { soloSujetosExcluidos = false } = {}) {
    // Normalizar término de búsqueda para soportar DUI/NIT sin guiones
    const normalizedTerm = normalizeDocumentSearch(searchTerm)

    // encodeURIComponent() codifica caracteres especiales para URL
    // Ej: "María García" → "Mar%C3%ADa%20Garc%C3%ADa"
    let url = `/Receptores/search?searchTerm=${encodeURIComponent(normalizedTerm)}`
    if (soloSujetosExcluidos) url += '&soloSujetosExcluidos=true'
    const response = await api.get(url)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: create
   * ===========================================================================
   *
   * Crea un nuevo receptor (cliente).
   *
   * ENDPOINT: POST /api/Receptores
   *
   * PROCESO:
   * 1. Recibe datos del formulario en formato frontend
   * 2. Transforma a DTO del backend (mapToCreateDto)
   * 3. Envía al backend
   * 4. Retorna el receptor creado
   *
   * @param {Object} receptor - Datos del receptor en formato frontend
   * @returns {Promise<Object>} Receptor creado con ID asignado
   */
  async create(receptor) {
    // Transformar datos antes de enviar
    const dto = this.mapToCreateDto(receptor)
    const response = await api.post('/Receptores', dto)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: update
   * ===========================================================================
   *
   * Actualiza un receptor existente.
   *
   * ENDPOINT: PUT /api/Receptores/{id}
   *
   * @param {number} id - ID del receptor
   * @param {Object} receptor - Datos actualizados en formato frontend
   * @returns {Promise<Object>} Receptor actualizado
   */
  async update(id, receptor) {
    const dto = this.mapToUpdateDto(receptor)
    const response = await api.put(`/Receptores/${id}`, dto)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: stripDocumentHyphens
   * ===========================================================================
   *
   * Remueve los guiones de un número de documento.
   *
   * ¿POR QUÉ ES NECESARIO?
   * ----------------------
   * - El USUARIO ve: 0614-010190-101-0 (NIT con guiones, más legible)
   * - El BACKEND almacena: 06140101901010 (NIT sin guiones)
   * - El MH requiere: sin guiones en el JSON del DTE
   *
   * @param {string} documento - Documento con o sin guiones
   * @returns {string} Documento sin guiones
   *
   * EJEMPLOS:
   * stripDocumentHyphens("0614-010190-101-0") → "06140101901010"
   * stripDocumentHyphens("01234567-8") → "012345678"
   * stripDocumentHyphens("06140101901010") → "06140101901010" (ya estaba limpio)
   */
  stripDocumentHyphens(documento) {
    if (!documento) return ''
    // Regex /-/g busca TODOS los guiones (g = global) y los reemplaza por ''
    return documento.replace(/-/g, '')
  },

  /**
   * ===========================================================================
   * MÉTODO: mapToCreateDto
   * ===========================================================================
   *
   * Transforma datos del frontend al DTO de creación del backend.
   *
   * DTO = Data Transfer Object
   * Es un patrón donde se define exactamente qué datos acepta una operación.
   * El backend define CreateReceptorDto con campos específicos.
   *
   * ESTRUCTURA FRONTEND:
   * {
   *   tipoDocumento: "36",
   *   numDocumento: "0614-010190-101-0",
   *   nombre: "Empresa ABC",
   *   direccion: { departamento, municipio, complemento },
   *   correo: "info@abc.com",
   *   telefono: "2222-3333"
   * }
   *
   * ESTRUCTURA BACKEND (CreateReceptorDto):
   * {
   *   tipoDocumento: "36",
   *   numeroDocumento: "06140101901010",  // SIN guiones
   *   nombreRazonSocial: "Empresa ABC",
   *   direccion: "Calle Principal...",    // String plano
   *   correoElectronico: "info@abc.com",
   *   telefono: "2222-3333"
   * }
   *
   * @param {Object} receptor - Receptor en formato frontend
   * @returns {Object} CreateReceptorDto para el backend
   */
  mapToCreateDto(receptor) {
    // El tipoDocumento ya viene como ID de BD (convertido en la vista desde código MH)
    // IMPORTANTE: La vista debe convertir el código MH a ID de BD antes de llamar este método
    const tipoDocId = receptor.tipoDocumento ? parseInt(receptor.tipoDocumento, 10) : null

    // Formatear número de documento según el tipo:
    // - NIT (ID 1): Backend espera 14 dígitos SIN guiones
    // - DUI (ID 2): Backend espera formato 00000000-0 CON guión
    // - Otros: mantener como está
    let numeroDoc = receptor.numDocumento || ''
    if (tipoDocId === 1 && numeroDoc) {
      // NIT: quitar todos los guiones
      numeroDoc = numeroDoc.replace(/-/g, '')
    }

    const dto = {
      // Tipo de documento como ID de BD (int)
      catTipoDocumentoId: tipoDocId,

      // Número de documento formateado según el tipo
      numeroDocumento: numeroDoc,

      // El backend usa "nombreRazonSocial", frontend usa "nombre"
      nombreRazonSocial: receptor.nombre || '',

      // NRC (opcional, solo para contribuyentes)
      // El backend espera solo dígitos (pattern ^[0-9]{1,8}$), sin guiones
      nrc: receptor.nrc ? receptor.nrc.replace(/[^0-9]/g, '') : null,

      // Actividad económica (opcional, requerido para CCF)
      codigoActividad: receptor.codigoActividad || null,
      descripcionActividad: receptor.descripcionActividad || null,

      // Departamento y Municipio (IDs opcionales)
      catDepartamentoId: receptor.direccion?.departamento ? parseInt(receptor.direccion.departamento, 10) : null,
      catMunicipioId: receptor.direccion?.municipio ? parseInt(receptor.direccion.municipio, 10) : null,
      catDistritoId: receptor.direccion?.distrito ? parseInt(receptor.direccion.distrito, 10) : null,

      // Dirección como string (complemento)
      direccion: receptor.direccion?.complemento || '',

      // El backend usa "correoElectronico", frontend usa "correo"
      correoElectronico: receptor.correo || '',

      // Teléfono: limpiar guiones por si la BD tiene constraint de solo dígitos
      telefono: receptor.telefono ? receptor.telefono.replace(/[^0-9]/g, '') : null
    }

    // Agregar emisorId si se proporciona (para vincular con un emisor específico)
    if (receptor.emisorId) {
      dto.emisorId = parseInt(receptor.emisorId, 10)
    }

    return dto
  },

  /**
   * ===========================================================================
   * MÉTODO: mapToUpdateDto
   * ===========================================================================
   *
   * Transforma datos del frontend al DTO de actualización del backend.
   * Similar a mapToCreateDto pero incluye campo 'activo'.
   *
   * @param {Object} receptor - Receptor en formato frontend
   * @returns {Object} UpdateReceptorDto para el backend
   */
  mapToUpdateDto(receptor) {
    // El tipoDocumento ya viene como ID de BD (convertido en la vista desde código MH)
    // IMPORTANTE: La vista debe convertir el código MH a ID de BD antes de llamar este método
    const tipoDocId = receptor.tipoDocumento ? parseInt(receptor.tipoDocumento, 10) : null

    // Formatear número de documento según el tipo:
    // - NIT (ID 1): Backend espera 14 dígitos SIN guiones
    // - DUI (ID 2): Backend espera formato 00000000-0 CON guión
    // - Otros: mantener como está
    let numeroDoc = receptor.numDocumento || ''
    if (tipoDocId === 1 && numeroDoc) {
      // NIT: quitar todos los guiones
      numeroDoc = numeroDoc.replace(/-/g, '')
    }

    return {
      // Tipo de documento como ID de BD (int)
      catTipoDocumentoId: tipoDocId,

      numeroDocumento: numeroDoc,
      nombreRazonSocial: receptor.nombre || '',

      // NRC (opcional, solo para contribuyentes)
      // El backend espera solo dígitos (pattern ^[0-9]{1,8}$), sin guiones
      nrc: receptor.nrc ? receptor.nrc.replace(/[^0-9]/g, '') : null,

      // Actividad económica (opcional, requerido para CCF)
      codigoActividad: receptor.codigoActividad || null,
      descripcionActividad: receptor.descripcionActividad || null,

      // Departamento y Municipio (IDs opcionales)
      catDepartamentoId: receptor.direccion?.departamento ? parseInt(receptor.direccion.departamento, 10) : null,
      catMunicipioId: receptor.direccion?.municipio ? parseInt(receptor.direccion.municipio, 10) : null,
      catDistritoId: receptor.direccion?.distrito ? parseInt(receptor.direccion.distrito, 10) : null,

      // Dirección como string (complemento)
      direccion: receptor.direccion?.complemento || '',

      correoElectronico: receptor.correo || '',
      // Teléfono: limpiar guiones por si la BD tiene constraint de solo dígitos
      telefono: receptor.telefono ? receptor.telefono.replace(/[^0-9]/g, '') : null,

      // Campo adicional para soft delete / activar-desactivar
      activo: receptor.activo !== false
    }
  },

  /**
   * ===========================================================================
   * MÉTODO: buildDireccionString
   * ===========================================================================
   *
   * Construye una cadena de dirección a partir del objeto de dirección.
   *
   * El frontend maneja dirección como objeto:
   * { departamento, municipio, complemento }
   *
   * El backend la almacena como string simple.
   *
   * @param {Object} receptor - Receptor con datos de dirección
   * @returns {string} Dirección como string
   */
  buildDireccionString(receptor) {
    const partes = []
    // Por ahora solo usamos el complemento
    // En el futuro podríamos concatenar departamento + municipio + complemento
    if (receptor.direccion?.complemento) {
      partes.push(receptor.direccion.complemento)
    }
    return partes.join(', ') || ''
  },

  /**
   * ===========================================================================
   * MÉTODO: formatDocument
   * ===========================================================================
   *
   * Formatea un documento agregando guiones según su tipo.
   *
   * PROCESO INVERSO A stripDocumentHyphens:
   * - Backend almacena: 06140101901010
   * - Usuario ve: 0614-010190-101-0
   *
   * DETECCIÓN POR LONGITUD:
   * - 14 caracteres → NIT (formato: 0000-000000-000-0)
   * - 9 caracteres → DUI (formato: 00000000-0)
   *
   * @param {string} documento - Documento sin guiones
   * @returns {string} Documento formateado con guiones
   *
   * EJEMPLOS:
   * formatDocument("06140101901010") → "0614-010190-101-0"
   * formatDocument("012345678") → "01234567-8"
   */
  formatDocument(documento) {
    if (!documento) return ''

    // Limpiar cualquier caracter no alfanumérico
    // [^a-zA-Z0-9] = cualquier cosa que NO sea letra o número
    const clean = documento.replace(/[^a-zA-Z0-9]/g, '')

    // NIT: 14 caracteres → XXXX-XXXXXX-XXX-X
    if (clean.length === 14) {
      // slice(start, end) extrae una porción del string
      return `${clean.slice(0, 4)}-${clean.slice(4, 10)}-${clean.slice(10, 13)}-${clean.slice(13)}`
    }
    // DUI: 9 caracteres → XXXXXXXX-X
    else if (clean.length === 9) {
      return `${clean.slice(0, 8)}-${clean.slice(8)}`
    }

    // Si no coincide con ningún formato conocido, retornar tal cual
    return documento
  },

  /**
   * ===========================================================================
   * MÉTODO: detectTipoDocumento
   * ===========================================================================
   *
   * Detecta el tipo de documento basándose en su longitud.
   *
   * TIPOS DE DOCUMENTO (CAT-022):
   * - "13": DUI (9 dígitos)
   * - "36": NIT (14 dígitos)
   * - "37": Otro
   * - "02": Pasaporte
   * - "03": Carné de residente
   *
   * @param {string} documento - Número de documento (con o sin guiones)
   * @returns {string} Código del tipo de documento
   */
  detectTipoDocumento(documento) {
    if (!documento) return '36' // NIT por defecto

    // Limpiar para contar solo dígitos
    const clean = documento.replace(/[^a-zA-Z0-9]/g, '')

    if (clean.length === 9) return '13'  // DUI
    if (clean.length === 14) return '36' // NIT

    return '36' // Default: NIT
  },

  /**
   * ===========================================================================
   * MÉTODO: mapFromDto
   * ===========================================================================
   *
   * Transforma la respuesta del backend al formato del frontend.
   *
   * PROCESO INVERSO A mapToCreateDto/mapToUpdateDto.
   *
   * ¿POR QUÉ ES NECESARIO?
   * ----------------------
   * - El backend usa camelCase (numeroDocumento)
   * - A veces el backend retorna PascalCase (NumeroDocumento)
   * - El frontend tiene estructura propia (numDocumento)
   * - Necesitamos formatear documentos con guiones
   *
   * @param {Object} dto - DTO del backend
   * @returns {Object} Receptor en formato frontend
   */
  mapFromDto(dto) {
    // Obtener número de documento, verificando ambos casos (camel y Pascal)
    const numDocRaw = dto.numeroDocumento || dto.NumeroDocumento || ''
    const direccionStr = dto.direccion || dto.Direccion || ''

    // Obtener el nombre del tipo de documento directamente del backend
    // El backend envía tipoDocumentoNombre: "NIT", "DUI", etc.
    const tipoDocNombre = dto.tipoDocumentoNombre || dto.TipoDocumentoNombre || ''

    // Mapeo de nombre de tipo de documento a código MH
    // Esto es necesario porque catTipoDocumentoId puede venir como 0 del backend
    const nombreToCodigo = {
      'NIT': '36',
      'DUI': '13',
      'Pasaporte': '03',
      'Carnet de Residente': '02',
      'Otro': '37'
    }

    // Obtener tipo de documento del backend (viene como CatTipoDocumentoId: int)
    // IMPORTANTE: El backend puede enviar catTipoDocumentoId como 0 aunque en BD sea 1
    // Por eso preferimos usar tipoDocumentoNombre para determinar el código correcto
    let tipoDocId = dto.catTipoDocumentoId ?? dto.CatTipoDocumentoId ?? null
    let tipoDoc = null

    // Prioridad 1: Usar tipoDocumentoNombre si está disponible (más confiable)
    if (tipoDocNombre && nombreToCodigo[tipoDocNombre]) {
      tipoDoc = nombreToCodigo[tipoDocNombre]
    }
    // Prioridad 2: Usar catTipoDocumentoId si es válido (> 0)
    else if (tipoDocId !== null && tipoDocId > 0) {
      tipoDoc = String(tipoDocId)
    }
    // Prioridad 3: Detectar por longitud del documento
    else {
      tipoDoc = this.detectTipoDocumento(numDocRaw)
    }

    // Obtener IDs de departamento, municipio y distrito (vienen como int o null)
    const deptoId = dto.catDepartamentoId ?? dto.CatDepartamentoId ?? null
    const muniId = dto.catMunicipioId ?? dto.CatMunicipioId ?? null
    const distId = dto.catDistritoId ?? dto.CatDistritoId ?? null

    return {
      // ID del receptor
      id: dto.id || dto.Id,

      // Tipo de documento (código MH como string: "36", "13", etc.)
      tipoDocumento: tipoDoc,

      // Nombre del tipo de documento (viene directo del backend: "NIT", "DUI", etc.)
      tipoDocumentoNombre: tipoDocNombre,

      // Número formateado con guiones para mostrar al usuario
      numDocumento: this.formatDocument(numDocRaw),

      // Nombre/Razón social
      nombre: dto.nombreRazonSocial || dto.NombreRazonSocial || '',

      // Campos adicionales para contribuyentes
      nrc: dto.nrc || dto.Nrc || '',
      codigoActividad: dto.codigoActividad || dto.CodigoActividad || '',
      descripcionActividad: dto.descripcionActividad || dto.DescripcionActividad || '',
      actividadEconomica: null, // Campo legacy mantenido para compatibilidad

      // Dirección como objeto con IDs de catálogo
      direccion: {
        departamento: deptoId !== null ? String(deptoId) : '',
        municipio: muniId !== null ? String(muniId) : '',
        distrito: distId !== null ? String(distId) : '',
        complemento: direccionStr
      },

      // Nombres de departamento/municipio/distrito para mostrar
      departamentoNombre: dto.departamentoNombre || dto.DepartamentoNombre || '',
      municipioNombre: dto.municipioNombre || dto.MunicipioNombre || '',
      distritoNombre: dto.distritoNombre || dto.DistritoNombre || '',

      telefono: dto.telefono || dto.Telefono || '',
      correo: dto.correoElectronico || dto.CorreoElectronico || '',

      // Contribuyente = tiene NRC (requerido para CCF). NIT sin NRC es consumidor final.
      esContribuyente: !!(dto.nrc || dto.Nrc),

      // Estado activo/inactivo
      activo: (dto.activo !== undefined ? dto.activo : dto.Activo) !== false,

      // Fechas de auditoría
      fechaCreacion: dto.fechaCreacion || dto.FechaCreacion,
      fechaModificacion: null
    }
  },

  /**
   * ===========================================================================
   * MÉTODO: mapListFromDto
   * ===========================================================================
   *
   * Transforma una lista de DTOs del backend al formato frontend.
   * Aplica mapFromDto a cada elemento.
   *
   * @param {Array} dtos - Lista de DTOs del backend
   * @returns {Array} Lista de receptores en formato frontend
   */
  mapListFromDto(dtos) {
    // map() aplica una función a cada elemento y retorna nuevo array
    return dtos.map(dto => this.mapFromDto(dto))
  },

  /**
   * ===========================================================================
   * MÉTODO: toggleActive
   * ===========================================================================
   *
   * Activa o desactiva un receptor (soft delete).
   *
   * ENDPOINT: PATCH /api/Receptores/{id}/toggle-active
   *
   * @param {number} id - ID del receptor
   * @returns {Promise<Object>} Receptor actualizado
   */
  async toggleActive(id) {
    const response = await api.patch(`/Receptores/${id}/toggle-active`)
    return response.data
  }
}

/**
 * =====================================================================================
 * EXPORTACIÓN DEL SERVICIO
 * =====================================================================================
 *
 * USO EN OTROS ARCHIVOS:
 * ----------------------
 * import receptoresService from '@/services/receptores.service'
 *
 * // Buscar clientes para autocomplete
 * const clientes = await receptoresService.search("empresa")
 *
 * // Crear nuevo cliente
 * const nuevoCliente = await receptoresService.create({
 *   tipoDocumento: '36',
 *   numDocumento: '0614-010190-101-0',
 *   nombre: 'Empresa ABC S.A.'
 * })
 *
 * // Formatear documento para mostrar
 * const formatted = receptoresService.formatDocument("06140101901010")
 * // Resultado: "0614-010190-101-0"
 *
 * ARCHIVOS QUE USAN ESTE SERVICIO:
 * --------------------------------
 * - src/views/configuracion/ReceptoresView.vue → CRUD completo
 * - src/views/emision/FacturaView.vue → Selección al emitir
 * - src/components/emision/DatosGeneralesFactura.vue → Autocomplete
 * - src/services/facturas.service.js → Mapeo de receptor en create()
 */
export default receptoresService
